import { Component, DoCheck, Input } from '@angular/core';
import { AufnahmePunktDocument, HorizontZeile, SubstratZeile, bodenformString, wasserformString, hatHumusForm, hatVegetationsformengruppe, humusformLabel, isB, isG, isM, isMudde, isQuellsediment, isTorf, moorMaechtigkeit, roundToNumber, roundToString, stfgString } from '../../../data-model';
import { AuthService } from 'src/app/auth.service';
import { DataService } from 'src/app/data.service';
import { EvaluationService } from '../../evaluation.service';
import { ValidationService } from '../../validation.service';
import { schichtNWSKEval } from '../../evaluators/nwsk';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-boden',
    templateUrl: './bohrpunkt-boden.component.html',
    styleUrls: ['../hl-table.component.css', './bohrpunkt-boden.component.css'],
    
})
export class BohrpunktBodenComponent implements DoCheck {

  @Input() bp!: AufnahmePunktDocument;
  @Input() printView!: boolean;

	roundToNumber = roundToNumber;
	roundToString = roundToString;
	isTorf = isTorf;
	isMudde = isMudde;
	isQuellsediment = isQuellsediment;
	humusformLabel = humusformLabel;
	hatHumusForm = hatHumusForm;
	hatVegetationsformengruppe = hatVegetationsformengruppe;
	wasserformString = wasserformString;
	moorMaechtigkeit = moorMaechtigkeit;

  // Cache für merged rows
  mergedRows: {substratIndex: number | null, horizontIndex: number | null, tiefe: number, substratStripe: boolean, horizontStripe: boolean}[] = [];
  private _lastSubstratTiefen = 'FORCE_INITIAL_COMPUTE';
  private _lastHorizontTiefen = 'FORCE_INITIAL_COMPUTE';

  constructor(
		public aus: AuthService,
		private ds: DataService,
		private es: EvaluationService,
		private vs: ValidationService,
	) { }

  ngDoCheck(): void {
    // Defensive Initialisierung fehlender Objekte (z.B. bei älteren oder importierten Punkten)
    if (!this.bp.extendedHeader.ph) {
      this.bp.extendedHeader.ph = { wert1: undefined, ort1: '', wert2: undefined, ort2: '', durchschnitt: undefined };
    }
    if (!this.bp.extendedHeader.humusAuflage) {
      this.bp.extendedHeader.humusAuflage = { ol: 0, of: 0, oh: 0 };
    }

    this.es.setContext(this.bp);
    this.vs.setContext(this.bp);
    this.bp.extendedHeader.nWsk = this.es.evaluate("nWSK") as number;
    this.updateMergedRowsIfNeeded();
  }

  private updateMergedRowsIfNeeded(): void {
    const substratTiefen = this.bp.Substratfolge?.map(s => s.tiefe).join(',') ?? '';
    const horizontTiefen = this.bp.Horizontfolge?.map(h => h.tiefe).join(',') ?? '';

    if (substratTiefen !== this._lastSubstratTiefen || horizontTiefen !== this._lastHorizontTiefen) {
      this.mergedRows = this.computeMergedRows();
      this._lastSubstratTiefen = substratTiefen;
      this._lastHorizontTiefen = horizontTiefen;
    }
  }

  stfg() {
		return stfgString(this.bp);
  }

  onAddSubstratZeile() {
    this.bp.Substratfolge.push({} as SubstratZeile);
  }

  onRemoveSubstratZeile(i: number) {
    this.bp.Substratfolge.splice(i, 1);
  }

  onAddHorizontZeile() {
    this.bp.Horizontfolge.push({} as HorizontZeile);
  }

  onRemoveHorizontZeile(i: number) {
    this.bp.Horizontfolge.splice(i, 1);
  }

  isB() {
    return isB(this.bp);
  }

  isG() {
    return isG(this.bp);
  }

  isM() {
    return isM(this.bp);
  }

	bodenformString(which: string) {
		return bodenformString(this.bp.header.bodenform, which);
	}

	qualityString(quality: number) {
		const e = this.ds.hardData.Qualitätsstufen.find((entry: any) => entry.id === quality);
		return e ? e.name : "unbekannt";
	}
	
	colSpanSubstrate(): number {
		// B: 9 Spalten (keine Farbe), M: 15 Spalten (3 Torfarten statt 1), G: 10 Spalten (mit Farbe)
		return isB(this.bp) ? 9 : (isM(this.bp) ? 15 : 10);
	}

  hauptbodenform = () => this.bodenformString('hbf');
  feinbodenform = () => {
    const fbf = this.bodenformString('fbf');
    const mk = this.bp.extendedHeader?.mesoklima;
    return mk ? `${fbf}_${mk}` : fbf;
  };

  ph1Label(): string {
    const ort = this.bp.extendedHeader.ph?.ort1;
    return ort ? `pH₁ (${ort})` : 'pH₁';
  }

  karbonattiefe(): number | undefined {
    return this.es.evaluate("Karbonattiefe") as number | undefined;
  }

  vWert(): string | undefined {
    const labor = this.bp.extendedHeader.vWertSaugsaum;
    const vermutet = this.bp.extendedHeader.vWertSaugsaumVermutet;
    if (labor !== undefined && labor !== null) {
      return String(labor);
    }
    return vermutet;
  }

  vWertLabel(): string {
    const labor = this.bp.extendedHeader.vWertSaugsaum;
    const vermutet = this.bp.extendedHeader.vWertSaugsaumVermutet;
    if (labor !== undefined && labor !== null) {
      return 'V-Wert (Labor)';
    }
    if (vermutet) {
      return 'V-Wert (geschätzt)';
    }
    return 'V-Wert';
  }

  schichtNWSK(i: number): number | undefined {
    // Direkt den Evaluator aufrufen, um Kontextwechsel-Probleme bei Multi-Print zu vermeiden
    const result = schichtNWSKEval(this.bp, i) as number | undefined;
    return result !== undefined ? Math.round(result) : undefined;
  }

  // Merged rows für synchronisierte Darstellung von Substrat- und Horizontfolge
  private computeMergedRows(): {substratIndex: number | null, horizontIndex: number | null, tiefe: number, substratStripe: boolean, horizontStripe: boolean}[] {
    const substratTiefen = this.bp.Substratfolge.map((s, i) => ({ tiefe: s.tiefe, type: 'substrat' as const, index: i }));
    const horizontTiefen = this.bp.Horizontfolge.map((h, i) => ({ tiefe: h.tiefe, type: 'horizont' as const, index: i }));

    // Alle einzigartigen Tiefen sammeln und sortieren
    const alleTiefen = new Set<number>();
    substratTiefen.forEach(s => { if (s.tiefe !== undefined) alleTiefen.add(s.tiefe); });
    horizontTiefen.forEach(h => { if (h.tiefe !== undefined) alleTiefen.add(h.tiefe); });

    const sortedTiefen = Array.from(alleTiefen).sort((a, b) => a - b);

    // Für jede Tiefe prüfen, ob Substrat und/oder Horizont existiert
    const result: {substratIndex: number | null, horizontIndex: number | null, tiefe: number, substratStripe: boolean, horizontStripe: boolean}[] = [];

    // Tracke, welche Indizes bereits verwendet wurden
    const usedSubstratIndices = new Set<number>();
    const usedHorizontIndices = new Set<number>();

    // Erste Zeile: Immer Index 0 für beide (falls vorhanden)
    const firstSubstrat = substratTiefen.length > 0 ? substratTiefen[0] : null;
    const firstHorizont = horizontTiefen.length > 0 ? horizontTiefen[0] : null;

    if (firstSubstrat || firstHorizont) {
      if (firstSubstrat) usedSubstratIndices.add(0);
      if (firstHorizont) usedHorizontIndices.add(0);

      // Tiefe für die erste Zeile: das Minimum der beiden ersten Tiefen (falls definiert)
      const firstTiefe = Math.min(
        firstSubstrat?.tiefe ?? Infinity,
        firstHorizont?.tiefe ?? Infinity
      );

      result.push({
        substratIndex: firstSubstrat ? 0 : null,
        horizontIndex: firstHorizont ? 0 : null,
        tiefe: firstTiefe === Infinity ? 0 : firstTiefe,
        substratStripe: false, // wird später berechnet
        horizontStripe: false  // wird später berechnet
      });
    }

    // Restliche Zeilen nach Tiefe sortiert
    for (const tiefe of sortedTiefen) {
      const substrat = substratTiefen.find(s => s.tiefe === tiefe && !usedSubstratIndices.has(s.index));
      const horizont = horizontTiefen.find(h => h.tiefe === tiefe && !usedHorizontIndices.has(h.index));

      // Überspringe, wenn beide null sind (wurde bereits in erster Zeile behandelt)
      if (!substrat && !horizont) continue;

      if (substrat) usedSubstratIndices.add(substrat.index);
      if (horizont) usedHorizontIndices.add(horizont.index);

      result.push({
        substratIndex: substrat ? substrat.index : null,
        horizontIndex: horizont ? horizont.index : null,
        tiefe,
        substratStripe: false, // wird später berechnet
        horizontStripe: false  // wird später berechnet
      });
    }

    // Stripes berechnen: Von unten nach oben
    // Echte Zeilen bekommen abwechselnd Farben, Leerzeilen bekommen die Farbe der echten Zeile DARUNTER
    let currentSubstratStripe = false;
    let currentHorizontStripe = false;

    for (let i = result.length - 1; i >= 0; i--) {
      const row = result[i];

      // Substrat: Erst wechseln bei echter Zeile, dann Farbe zuweisen
      if (row.substratIndex !== null) {
        currentSubstratStripe = !currentSubstratStripe;
      }
      row.substratStripe = currentSubstratStripe;

      // Horizont: gleiche Logik
      if (row.horizontIndex !== null) {
        currentHorizontStripe = !currentHorizontStripe;
      }
      row.horizontStripe = currentHorizontStripe;
    }

    return result;
  }

  getSubstratRow(index: number | null): SubstratZeile | null {
    return index !== null ? this.bp.Substratfolge[index] : null;
  }

  getHorizontRow(index: number | null): HorizontZeile | null {
    return index !== null ? this.bp.Horizontfolge[index] : null;
  }
}
